import { useManager } from 'dreamstate';
import { default as clamp } from 'lodash/clamp';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  ChangeEvent,
  ReactElement,
  useLayoutEffect,
} from 'react';

import { Box, Flex, SystemStyleObject, rem } from '@vimeo/bokeh';
import { Chats } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Definable, Optional } from '@vimeo/core/types/generic';

import { interactionToolsConfig } from '../../../../configs/InteractionToolsConfig';
import { useScrollbarStyles } from '../../../../hooks/styling/useScrollbarStyles';
import { ChatManager } from '../../../../store/firebase/interaction/chat';
import {
  trackBanUser,
  trackDeleteBackstageMessage,
  trackDeleteMessage,
  trackViewChat,
} from '../../../../tracking/tracking_engagement_chat';
import { T_CHAT_PLACEHOLDER } from '../../../../translations';
import { EChatType, IChatMessage, IChatUser } from '../../../../types/interaction_tools';
import { createDomName } from '../../../../utils/dom/identifying';
import { getElementVerticalScroll } from '../../../../utils/dom/scroll';
import { EmptyStatePlaceholder } from '../../../panels/empty';
import { ChatMessage } from '../chat_message';
import { ChatHistoryModal } from './ChatHistoryModal';
import { ChatHistoryNotice } from './ChatHistoryNotice';
import { useChatHistoryMessages } from './useChatHistoryMessages';
import { IModalProps, useModalProps } from './useModalProps';
import { INoticeProps, useNoticeProps } from './useNoticeProps';

/**
 * Threshold distance from bottom of chat history scroll.
 * If user is scrolled more than it, we do not auto-scroll.
 *
 * In this case we:
 * (1) allow users to have auto scroll when single message appears
 * (2) allow users to scroll history without interrupting and auto scroll on new messages
 */
const DY_THRESHOLD_MIN: number = 200;
const DY_THRESHOLD_MAX: number = 600;

export interface IChatHistoryProps extends IBaseDomElementProps {
  chatType: EChatType;
  currentUserId: string;
  scrollBackground?: string | undefined;
  placeholder?: ReactElement;
}

export function ChatHistory({
  id = createDomName('chat-history'),
  className = createDomName('chat-history'),
  chatType,
  currentUserId,
  scrollBackground = 'surface',
  placeholder,
}: IChatHistoryProps) {
  const {
    chatActions: { deleteMessage, blockChatUser, unblockChatUser },
    config: { isCreator },
    [chatType]: { participants, messages: rawMessages, banList },
  } = useManager(ChatManager);

  const messages: Array<IChatMessage> = useChatHistoryMessages(rawMessages);

  const canAskToDeleteMessageConfirmationBeShown =
    interactionToolsConfig.CHAT.ASK_TO_DELETE_CHAT_MESSAGE;
  const noticeTimer = useRef<number>();
  const currentLastMessageCreatedAtRef = useRef<number>();
  const chatHistoryRef = useRef<HTMLUListElement>(null);
  const chatHistoryBottomRef = useRef<HTMLLIElement>(null);

  const [deleteMessageId, setDeleteMessageId] = useState<Optional<string>>(null);
  const [blockUserId, setBlockUserId] = useState<Optional<string>>(null);
  const [blockedUser, setBlockedUser] = useState<Optional<string>>(null);
  const [showNewMessageNotice, setShowNewMessageNotice] = useState<boolean>(false);
  const [showBlockedUserNotice, setShowBlockedUserNotice] = useState<boolean>(false);
  const [isChatHistoryVisible, setChatHistoryVisible] = useState<boolean>(false);

  const scrollChatHistoryToBottom = useCallback(
    (isSmooth: boolean = true) => {
      if (chatHistoryBottomRef.current && chatHistoryRef.current) {
        const container = chatHistoryRef.current;

        container.scroll({
          top: container.scrollHeight,
          behavior: isSmooth ? 'smooth' : 'auto',
        });
      }
      if (!isChatHistoryVisible) {
        setChatHistoryVisible(true);
      }
    },
    [isChatHistoryVisible, setChatHistoryVisible],
  );

  // Handle scrolling down to not show 'you have messages' when scrolled to bottom.
  const handleChatHistoryScroll = useCallback(() => {
    const chatHistoryElement = chatHistoryRef.current;

    if (!chatHistoryElement) {
      return;
    }

    if (getElementVerticalScroll(chatHistoryElement) < DY_THRESHOLD_MIN && showNewMessageNotice) {
      setShowNewMessageNotice(false);
    }
  }, [showNewMessageNotice, setShowNewMessageNotice]);

  const handleNewMessage = useCallback(() => {
    if (!messages || !messages.length) {
      return;
    }

    const lastMessage = messages[messages.length - 1];

    if (!lastMessage || currentLastMessageCreatedAtRef.current === lastMessage.createdAt) {
      return;
    }

    currentLastMessageCreatedAtRef.current = lastMessage.createdAt;

    const chatHistoryElement: Optional<HTMLUListElement> = chatHistoryRef.current;

    if (!chatHistoryElement) {
      return;
    }

    const chatHistoryScroll: number = getElementVerticalScroll(chatHistoryElement);
    const lastMessageElement: Element =
      chatHistoryElement.children[chatHistoryElement.childElementCount - 2];

    if (!chatHistoryScroll || !lastMessageElement) {
      if (!isChatHistoryVisible) {
        setChatHistoryVisible(true);
      }

      return;
    }

    // Verify if message height is in allowed scroll bounds, and we can auto-scroll
    // - Add minimal DY_THRESHOLD to account minimalistic changes with small messages
    // - Respect whole scroll size, if not scrolled more than height of history, automatically scroll
    // - Respect message size and if width is small (height will be big), still scroll
    if (
      chatHistoryScroll <
      clamp(
        Math.max(lastMessageElement.clientHeight * 2, chatHistoryElement.clientHeight),
        DY_THRESHOLD_MIN,
        DY_THRESHOLD_MAX,
      )
    ) {
      return scrollChatHistoryToBottom();
    }

    if (currentUserId === lastMessage.senderId) {
      return scrollChatHistoryToBottom();
    }

    if (!showNewMessageNotice) {
      setShowNewMessageNotice(true);
    }
  }, [
    currentUserId,
    isChatHistoryVisible,
    messages,
    scrollChatHistoryToBottom,
    showNewMessageNotice,
  ]);

  const handleDeleteConfirm = useCallback(() => {
    if (deleteMessageId) {
      deleteMessage(chatType, deleteMessageId);
      setDeleteMessageId(null);
      chatType === EChatType.PUBLIC ? trackDeleteMessage() : trackDeleteBackstageMessage();
    }
  }, [chatType, deleteMessageId, setDeleteMessageId, deleteMessage]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteMessageId(null);
  }, [setDeleteMessageId]);

  const handleBlockUserConfirm = useCallback(() => {
    if (blockUserId) {
      blockChatUser(chatType, blockUserId);
      setBlockedUser(participants[blockUserId].displayName);
      setBlockUserId(null);
      setShowBlockedUserNotice(true);
      trackBanUser();
    }
  }, [
    chatType,
    blockUserId,
    participants,
    blockChatUser,
    setBlockUserId,
    setShowBlockedUserNotice,
  ]);

  const handleBlockUserCancel = useCallback(() => {
    setBlockUserId(null);
  }, [setBlockUserId]);

  const onCheckAskAgain = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    interactionToolsConfig.CHAT.ASK_TO_DELETE_CHAT_MESSAGE = !event.target.checked;
  }, []);

  const handleBlockUser = useCallback(
    (userId: string) => {
      if (!userId) return;

      setBlockUserId(userId);
    },
    [setBlockUserId],
  );

  const handleUnblockUser = useCallback(
    (userId: string) => unblockChatUser(chatType, userId),
    [chatType, unblockChatUser],
  );

  const handleCloseBlockUserNotice = useCallback(() => {
    setShowBlockedUserNotice(false);
    setBlockedUser(null);
  }, [setShowBlockedUserNotice, setBlockedUser]);

  const checkUserBan = useCallback(
    (senderId: string): boolean => {
      if (!banList) return false;

      return Object.values(banList).some(({ userId }) => userId === senderId);
    },
    [banList],
  );

  useEffect(() => {
    scrollChatHistoryToBottom(false);
    trackViewChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen only message changes
  useLayoutEffect(() => {
    if (messages.length) {
      handleNewMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    if (showBlockedUserNotice) noticeTimer.current = setTimeout(handleCloseBlockUserNotice, 2000);

    return () => clearTimeout(noticeTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBlockedUserNotice]);

  useEffect(() => {
    if (!canAskToDeleteMessageConfirmationBeShown && Boolean(deleteMessageId)) {
      handleDeleteConfirm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteMessageId]);

  const noticeProps: INoticeProps = useNoticeProps({
    showNewMessageNotice,
    scrollChatHistoryToBottom,
    showBlockedUserNotice,
    blockedUser,
    handleCloseBlockUserNotice,
  });

  const modalProps: IModalProps = useModalProps({
    deleteMessageId,
    handleDeleteCancel,
    handleDeleteConfirm,
    blockUserId,
    handleBlockUserCancel,
    handleBlockUserConfirm,
    onCheckAskAgain,
  });

  const isModalShown: boolean =
    (canAskToDeleteMessageConfirmationBeShown && Boolean(deleteMessageId)) || Boolean(blockUserId);
  const isChatPublic: boolean = chatType === EChatType.PUBLIC;

  const scrollbarStyles: SystemStyleObject = useScrollbarStyles({
    scrollbarColor: scrollBackground,
  });

  if (!messages.length)
    return (
      placeholder || (
        <EmptyStatePlaceholder
          id={createDomName(id, 'placeholder')}
          className={createDomName(className, 'placeholder')}
          icon={<Chats boxSize={'lg'} />}
          description={T_CHAT_PLACEHOLDER[chatType].text}
          control={null}
        />
      )
    );

  return (
    <Flex
      id={id}
      className={className}
      flexGrow={1}
      flexDirection={'column'}
      alignItems={'center'}
      justifyContent={'flex-end'}
      overflowY={'hidden'}
      width={'100%'}
      paddingBottom={rem(8)}
      sx={{
        contentVisibility: 'auto',
      }}
    >
      <Box
        as={'ul'}
        ref={chatHistoryRef}
        width={'100%'}
        flexGrow={0}
        overflowY={'scroll'}
        transition={'opacity 400ms ease'}
        opacity={isChatHistoryVisible ? 1 : 0}
        sx={scrollbarStyles}
        onScroll={handleChatHistoryScroll}
      >
        {messages.map((message) => {
          const sender: IChatUser = participants[message.senderId];
          /**
           * Do not display banned user messages until you are author of the message or creator.
           * Do not show blocked status for banned users and implement shadow ban in a such way.
           */
          const isBlocked: boolean =
            message.senderId !== currentUserId && checkUserBan(message.senderId);
          const isHiddenMessage: boolean =
            isChatPublic &&
            isBlocked &&
            !isCreator &&
            (!currentUserId || currentUserId !== message.senderId);
          const isCurrentSender: boolean = currentUserId === sender.id;

          const onBlockUser: Definable<(userId: string) => void> = isChatPublic
            ? handleBlockUser
            : undefined;
          const onUnblockUser: Definable<(userId: string) => Promise<void>> = isChatPublic
            ? handleUnblockUser
            : undefined;

          if (isHiddenMessage || !sender) {
            return null;
          }

          return (
            <ChatMessage
              key={message.id}
              message={message}
              sender={sender}
              isPublic={isChatPublic}
              isCurrentSender={isCurrentSender}
              isCreator={isCreator}
              isBlocked={isBlocked}
              onDeleteMessage={setDeleteMessageId}
              onBlockUser={onBlockUser}
              onUnblockUser={onUnblockUser}
            />
          );
        })}

        <Box
          ref={chatHistoryBottomRef}
          as={'li'}
          id={createDomName(id, 'history-bottom')}
          className={createDomName(className, 'history-bottom')}
        />
      </Box>

      <ChatHistoryNotice {...noticeProps} />

      {isModalShown ? <ChatHistoryModal {...modalProps} /> : null}
    </Flex>
  );
}
